import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Video,
  Calendar,
  Clock,
  User,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ExternalLink,
  ArrowLeft,
  FileText,
  PlayCircle,
  Edit2,
  Users,
  UserCheck,
  UserX,
  LogOut,
  LogIn,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "react-hot-toast";

const STATUS_CONFIG = {
  scheduled: { label: "Scheduled", color: "bg-blue-100 text-blue-700 border-blue-200", icon: Calendar },
  completed: { label: "Completed", color: "bg-green-100 text-green-700 border-green-200", icon: CheckCircle2 },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-700 border-red-200", icon: XCircle },
  pending_approval: { label: "Pending Approval", color: "bg-amber-100 text-amber-700 border-amber-200", icon: AlertCircle },
};

export default function MeetingDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [notesText, setNotesText] = useState("");
  const [recordingUrl, setRecordingUrl] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [savingRecording, setSavingRecording] = useState(false);
  const [attendanceSaving, setAttendanceSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    scheduled_at: "",
    duration_minutes: "30",
    zoom_link: "",
  });

  const isTeacher = user?.role === "teacher";

  useEffect(() => {
    fetchMeeting();
  }, [id]);

  const fetchMeeting = async () => {
    try {
      const res = await axios.get(`${API}/meetings/${id}`);
      setMeeting(res.data);
      setNotesText(res.data.notes || "");
      setRecordingUrl(res.data.recording_url || "");
      if (res.data.scheduled_at) {
        const dt = new Date(res.data.scheduled_at);
        const localIso = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
        setEditForm({
          title: res.data.title,
          description: res.data.description || "",
          scheduled_at: localIso,
          duration_minutes: String(res.data.duration_minutes || 30),
          zoom_link: res.data.zoom_link || "",
        });
      }
    } catch (err) {
      toast.error("Failed to load meeting");
      navigate("/meetings");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      await axios.post(`${API}/meetings/${id}/approve`, {});
      toast.success("Meeting approved");
      fetchMeeting();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve");
    }
  };

  const handleDecline = async () => {
    try {
      await axios.post(`${API}/meetings/${id}/decline`, { reason: cancelReason });
      toast.success("Meeting declined");
      fetchMeeting();
      setCancelOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to decline");
    }
  };

  const handleCancel = async () => {
    try {
      await axios.post(`${API}/meetings/${id}/cancel`, { reason: cancelReason });
      toast.success("Meeting cancelled");
      fetchMeeting();
      setCancelOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel");
    }
  };

  const handleComplete = async () => {
    try {
      await axios.post(`${API}/meetings/${id}/complete`);
      toast.success("Meeting marked as completed");
      fetchMeeting();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to complete");
    }
  };

  const handleUpdate = async () => {
    try {
      const payload = {
        title: editForm.title,
        description: editForm.description || null,
        scheduled_at: new Date(editForm.scheduled_at).toISOString(),
        duration_minutes: parseInt(editForm.duration_minutes),
        zoom_link: editForm.zoom_link || null,
      };
      await axios.put(`${API}/meetings/${id}`, payload);
      toast.success("Meeting updated");
      setEditOpen(false);
      fetchMeeting();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const handleSaveNotes = async () => {
    if (!notesText.trim()) return;
    setSavingNotes(true);
    try {
      await axios.put(`${API}/meetings/${id}/notes`, { notes: notesText });
      toast.success("Notes saved");
      fetchMeeting();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  const handleSaveRecording = async () => {
    if (!recordingUrl.trim()) return;
    setSavingRecording(true);
    try {
      await axios.put(`${API}/meetings/${id}/recording`, { recording_url: recordingUrl });
      toast.success("Recording URL saved");
      fetchMeeting();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save recording");
    } finally {
      setSavingRecording(false);
    }
  };

  const handleOptOut = async () => {
    try {
      await axios.post(`${API}/meetings/${id}/opt-out`);
      toast.success("Opted out of group class");
      fetchMeeting();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to opt out");
    }
  };

  const handleOptIn = async () => {
    try {
      await axios.post(`${API}/meetings/${id}/opt-in`);
      toast.success("Opted back into group class");
      fetchMeeting();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to opt in");
    }
  };

  const handleSaveAttendance = async () => {
    if (!meeting?.participants) return;
    setAttendanceSaving(true);
    try {
      const attendance = meeting.participants.map((p) => ({
        student_id: p.student_id,
        attended: p.attended ?? false,
      }));
      await axios.put(`${API}/meetings/${id}/attendance/bulk`, { attendance });
      toast.success("Attendance saved");
      fetchMeeting();
    } catch (err) {
      toast.error("Failed to save attendance");
    } finally {
      setAttendanceSaving(false);
    }
  };

  const toggleAttendance = (studentId) => {
    setMeeting((prev) => ({
      ...prev,
      participants: prev.participants.map((p) =>
        p.student_id === studentId ? { ...p, attended: !p.attended } : p
      ),
    }));
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex justify-center py-24">
          <div className="w-8 h-8 border-4 border-slate-200 border-t-amber-500 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!meeting) return null;

  const config = STATUS_CONFIG[meeting.status] || STATUS_CONFIG.scheduled;
  const StatusIcon = config.icon;
  const scheduledDate = meeting.scheduled_at ? parseISO(meeting.scheduled_at) : null;
  const isPending = meeting.status === "pending_approval";
  const isScheduled = meeting.status === "scheduled";
  const isCompleted = meeting.status === "completed";
  const isCancelled = meeting.status === "cancelled";
  const isGroup = meeting.meeting_type === "group";
  const participants = meeting.participants || [];
  const attendingCount = participants.filter((p) => p.status === "attending").length;
  const myParticipant = participants.find((p) => p.student_id === user?.user_id);
  const isOptedOut = myParticipant?.status === "opted_out";

  return (
    <Layout user={user}>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="meeting-detail-page">
        {/* Back button */}
        <Button
          data-testid="back-to-meetings-btn"
          variant="ghost"
          className="text-slate-500 hover:text-slate-700 -ml-2"
          onClick={() => navigate("/meetings")}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Meetings
        </Button>

        {/* Meeting Header Card */}
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-3">
                  <h1 className="font-serif text-2xl font-bold text-slate-900">{meeting.title}</h1>
                  {isGroup && (
                    <Badge className="bg-indigo-600 hover:bg-indigo-600 text-white text-xs">
                      <Users className="w-3 h-3 mr-1" />
                      Group Class
                    </Badge>
                  )}
                  <Badge variant="outline" className={`text-xs ${config.color}`}>
                    <StatusIcon className="w-3 h-3 mr-1" />
                    {config.label}
                  </Badge>
                </div>
                {meeting.initiated_by === "student" && (
                  <p className="text-xs text-amber-600 font-medium">Requested by student</p>
                )}
              </div>
              {isTeacher && (isScheduled || isPending) && (
                <div className="flex gap-2">
                  {isScheduled && (
                    <Button
                      data-testid="edit-meeting-btn"
                      variant="outline"
                      size="sm"
                      onClick={() => setEditOpen(true)}
                    >
                      <Edit2 className="w-3.5 h-3.5 mr-1.5" />
                      Edit
                    </Button>
                  )}
                </div>
              )}
            </div>

            <Separator className="my-4" />

            {/* Meeting Info Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoRow icon={User} label="Teacher" value={meeting.teacher_name} picture={meeting.teacher_picture} />
              {isGroup ? (
                <InfoRow icon={Users} label="Participants" value={`${attendingCount} attending`} />
              ) : (
                <InfoRow icon={User} label="Student" value={meeting.student_name} picture={meeting.student_picture} />
              )}
              {scheduledDate && (
                <InfoRow icon={Calendar} label="Date" value={format(scheduledDate, "EEEE, MMMM d, yyyy")} />
              )}
              {scheduledDate && (
                <InfoRow icon={Clock} label="Time" value={`${format(scheduledDate, "h:mm a")} (${meeting.duration_minutes} min)`} />
              )}
            </div>

            {meeting.description && (
              <div className="mt-4 p-4 bg-slate-50 rounded-xl">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">Agenda</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{meeting.description}</p>
              </div>
            )}

            {/* Zoom Link */}
            {meeting.zoom_link && (
              <div className="mt-4">
                <a
                  data-testid="join-zoom-btn"
                  href={meeting.zoom_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-full text-sm font-medium transition-colors shadow-md shadow-blue-600/20"
                >
                  <Video className="w-4 h-4" />
                  Join Zoom Meeting
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            )}

            {/* Cancellation info */}
            {isCancelled && meeting.cancellation_reason && (
              <div className="mt-4 p-4 bg-red-50 border border-red-100 rounded-xl">
                <p className="text-xs font-bold uppercase tracking-widest text-red-400 mb-1">Cancellation Reason</p>
                <p className="text-sm text-red-700">{meeting.cancellation_reason}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        {isPending && isTeacher && (
          <Card className="border border-amber-200 bg-amber-50">
            <CardContent className="p-5">
              <p className="text-sm text-amber-700 font-medium mb-3">
                {meeting.student_name} has requested this meeting. Would you like to approve or decline?
              </p>
              <div className="flex gap-3">
                <Button
                  data-testid="approve-meeting-btn"
                  className="bg-green-600 hover:bg-green-700 text-white rounded-full"
                  onClick={handleApprove}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Approve
                </Button>
                <Button
                  data-testid="decline-meeting-btn"
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50 rounded-full"
                  onClick={() => setCancelOpen(true)}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Decline
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {isScheduled && (
          <div className="flex gap-3">
            {isTeacher && (
              <Button
                data-testid="complete-meeting-btn"
                className="bg-green-600 hover:bg-green-700 text-white rounded-full"
                onClick={handleComplete}
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Mark as Completed
              </Button>
            )}
            <Button
              data-testid="cancel-meeting-btn"
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50 rounded-full"
              onClick={() => setCancelOpen(true)}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Cancel Meeting
            </Button>
          </div>
        )}

        {/* Group Class: Student Opt-out/Opt-in */}
        {isGroup && !isTeacher && myParticipant && (isScheduled || isPending) && (
          <div className="flex items-center gap-3">
            {isOptedOut ? (
              <Button
                data-testid="opt-in-btn"
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full"
                onClick={handleOptIn}
              >
                <LogIn className="w-4 h-4 mr-2" />
                Opt Back In
              </Button>
            ) : (
              <Button
                data-testid="opt-out-btn"
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50 rounded-full"
                onClick={handleOptOut}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Opt Out
              </Button>
            )}
            {isOptedOut && (
              <p className="text-sm text-red-500">You've opted out of this group class.</p>
            )}
          </div>
        )}

        {/* Group Participants & Attendance */}
        {isGroup && participants.length > 0 && (
          <Card className="border border-indigo-200 shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-indigo-600" />
                  Participants ({attendingCount})
                </CardTitle>
                {isTeacher && isCompleted && (
                  <Button
                    data-testid="save-attendance-btn"
                    size="sm"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full"
                    onClick={handleSaveAttendance}
                    disabled={attendanceSaving}
                  >
                    {attendanceSaving ? "Saving..." : "Save Attendance"}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {participants.map((p) => {
                  const isOpted = p.status === "opted_out";
                  return (
                    <div
                      key={p.student_id}
                      data-testid={`participant-${p.student_id}`}
                      className={`flex items-center justify-between p-3 rounded-xl ${isOpted ? "bg-slate-50 opacity-60" : "bg-indigo-50/50"}`}
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={p.picture} />
                          <AvatarFallback className="bg-indigo-100 text-indigo-700 text-xs">
                            {p.name?.charAt(0) || "?"}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium text-slate-800">{p.name}</p>
                          <p className={`text-xs ${isOpted ? "text-red-500" : "text-indigo-500"}`}>
                            {isOpted ? "Opted out" : "Attending"}
                          </p>
                        </div>
                      </div>
                      {/* Attendance toggle for teacher on completed meetings */}
                      {isTeacher && isCompleted && !isOpted && (
                        <button
                          data-testid={`attendance-toggle-${p.student_id}`}
                          onClick={() => toggleAttendance(p.student_id)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                            p.attended
                              ? "bg-green-100 text-green-700"
                              : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                          }`}
                        >
                          {p.attended ? <UserCheck className="w-3.5 h-3.5" /> : <UserX className="w-3.5 h-3.5" />}
                          {p.attended ? "Present" : "Absent"}
                        </button>
                      )}
                      {/* Show attendance status for students viewing completed meetings */}
                      {!isTeacher && isCompleted && p.attended !== null && p.student_id === user?.user_id && (
                        <Badge variant="outline" className={p.attended ? "text-green-600 border-green-200 bg-green-50" : "text-red-600 border-red-200 bg-red-50"}>
                          {p.attended ? "Present" : "Absent"}
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notes Section */}
        {(isCompleted || meeting.notes) && (
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-serif text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-600" />
                Meeting Notes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isTeacher && isCompleted ? (
                <div className="space-y-3">
                  <Textarea
                    data-testid="meeting-notes-textarea"
                    value={notesText}
                    onChange={(e) => setNotesText(e.target.value)}
                    placeholder="Add structured notes from this meeting..."
                    rows={6}
                    className="bg-slate-50 focus:bg-white"
                  />
                  <div className="flex items-center justify-between">
                    {meeting.notes_added_at && (
                      <p className="text-xs text-slate-400">
                        Last updated: {format(parseISO(meeting.notes_added_at), "MMM d, yyyy h:mm a")}
                      </p>
                    )}
                    <Button
                      data-testid="save-notes-btn"
                      onClick={handleSaveNotes}
                      disabled={savingNotes}
                      className="bg-amber-600 hover:bg-amber-700 text-white rounded-full ml-auto"
                      size="sm"
                    >
                      {savingNotes ? "Saving..." : "Save Notes"}
                    </Button>
                  </div>
                </div>
              ) : meeting.notes ? (
                <div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{meeting.notes}</p>
                  {meeting.notes_added_at && (
                    <p className="text-xs text-slate-400 mt-3">
                      Added: {format(parseISO(meeting.notes_added_at), "MMM d, yyyy h:mm a")}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-400 italic">No notes added yet.</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Recording Section */}
        {(isCompleted || meeting.recording_url) && (
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-serif text-lg flex items-center gap-2">
                <PlayCircle className="w-5 h-5 text-amber-600" />
                Recording
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isTeacher && isCompleted ? (
                <div className="space-y-3">
                  <div>
                    <Label>Zoom Recording URL</Label>
                    <Input
                      data-testid="recording-url-input"
                      value={recordingUrl}
                      onChange={(e) => setRecordingUrl(e.target.value)}
                      placeholder="https://zoom.us/rec/..."
                      className="bg-slate-50 focus:bg-white"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    {meeting.recording_added_at && (
                      <p className="text-xs text-slate-400">
                        Last updated: {format(parseISO(meeting.recording_added_at), "MMM d, yyyy h:mm a")}
                      </p>
                    )}
                    <Button
                      data-testid="save-recording-btn"
                      onClick={handleSaveRecording}
                      disabled={savingRecording}
                      className="bg-amber-600 hover:bg-amber-700 text-white rounded-full ml-auto"
                      size="sm"
                    >
                      {savingRecording ? "Saving..." : "Save Recording"}
                    </Button>
                  </div>
                </div>
              ) : meeting.recording_url ? (
                <div>
                  <a
                    data-testid="view-recording-link"
                    href={meeting.recording_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-full text-sm text-slate-700 transition-colors"
                  >
                    <PlayCircle className="w-4 h-4" />
                    View Recording
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                  {meeting.recording_added_at && (
                    <p className="text-xs text-slate-400 mt-2">
                      Added: {format(parseISO(meeting.recording_added_at), "MMM d, yyyy h:mm a")}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-400 italic">No recording added yet.</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Edit Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">Edit Meeting</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label>Title</Label>
                <Input
                  data-testid="edit-title-input"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                />
              </div>
              <div>
                <Label>Description / Agenda</Label>
                <Textarea
                  data-testid="edit-description-input"
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date & Time</Label>
                  <Input
                    data-testid="edit-datetime-input"
                    type="datetime-local"
                    value={editForm.scheduled_at}
                    onChange={(e) => setEditForm({ ...editForm, scheduled_at: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Duration</Label>
                  <Select
                    value={editForm.duration_minutes}
                    onValueChange={(val) => setEditForm({ ...editForm, duration_minutes: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 min</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Zoom Meeting Link</Label>
                <Input
                  data-testid="edit-zoom-input"
                  value={editForm.zoom_link}
                  onChange={(e) => setEditForm({ ...editForm, zoom_link: e.target.value })}
                  placeholder="https://zoom.us/j/..."
                />
              </div>
              <Button
                data-testid="save-edit-btn"
                className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded-full"
                onClick={handleUpdate}
              >
                Save Changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Cancel/Decline Confirmation */}
        <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {isPending ? "Decline Meeting Request?" : "Cancel Meeting?"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {isPending
                  ? "The student will be notified that their request was declined."
                  : "Both participants will be notified of the cancellation."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="my-2">
              <Label>Reason (optional)</Label>
              <Textarea
                data-testid="cancel-reason-input"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Add a reason..."
                rows={2}
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-full">Keep</AlertDialogCancel>
              <AlertDialogAction
                data-testid="confirm-cancel-btn"
                className="bg-red-600 hover:bg-red-700 text-white rounded-full"
                onClick={isPending ? handleDecline : handleCancel}
              >
                {isPending ? "Decline" : "Cancel Meeting"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}

function InfoRow({ icon: Icon, label, value, picture }) {
  return (
    <div className="flex items-center gap-3">
      {picture ? (
        <Avatar className="h-8 w-8">
          <AvatarImage src={picture} />
          <AvatarFallback className="bg-slate-100 text-slate-600 text-xs">
            {value?.charAt(0) || "?"}
          </AvatarFallback>
        </Avatar>
      ) : (
        <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
          <Icon className="w-4 h-4 text-slate-500" />
        </div>
      )}
      <div>
        <p className="text-xs text-slate-400">{label}</p>
        <p className="text-sm font-medium text-slate-700">{value || "—"}</p>
      </div>
    </div>
  );
}
